import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { PortalMessage } from '@components/portalMessage/PortalMessage';
import { PortalMessageType } from '@components/portalMessage/PortalMessageType.enum';
import Loading from '@components/Loading';
import { useCheckQuoteActiveBinding } from '@services/quotes/quoteBinding';
import { useQuote } from '@services/quotes';
import { useGetPolicy } from '@services/policy';
import { QuoteType, TQuoteApiResponse } from '@models/quotes';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { Box } from '@mui/material';

export const QuoteBindInProgressPage = () => {
  const { quoteId } = useParams();
  const { isCustomerUser } = useStore((state) => state.userAuth, shallow);

  invariant(quoteId);
  const { isLoading, quote } = useQuote(quoteId);
  const isEndorsement = quote?.quoteType === QuoteType.Endorsement;
  const { data: policy } = useGetPolicy({ policyId: isEndorsement ? quote.policyId : undefined });
  const { quoteBindings } = useCheckQuoteActiveBinding(quote as TQuoteApiResponse, {
    redirectToDetails: true,
    pollActive: true,
  });

  if (isLoading || !quote) {
    return <Loading />;
  }

  // Quote bindings should always have both of these, but this is to keep typescript from needing undefined checks everywhere.
  const filteredQuoteBindings = quoteBindings?.map((qb) => ({
    policyNumber: policy ? getFormattedPolicyNumber(policy.policyNumberPrefix, qb.policyNumber!) : qb.policyNumber,
    termPremium: qb.termPremium,
    status: qb.status,
    termTaxFeeSurcharge: qb.termTaxFeeSurcharge ?? 0,
  })) as {
    policyNumber: string;
    termPremium: number;
    termTaxFeeSurcharge: number;
  }[];

  // 0 just means we have not loaded yet and should not show the $ amount of a single policy as it may be wrong)
  const hasMultiplePolicies = filteredQuoteBindings.length === 0 || filteredQuoteBindings?.length > 1;

  const title =
    quote.quoteType === QuoteType.Endorsement
      ? ['Nice work, team.', hasMultiplePolicies ? 'The policies are being endorsed' : 'The policy is being endorsed.']
      : ["You're all set!", hasMultiplePolicies ? 'The policies are being bound' : 'The policy is being bound.'];

  let policyText;
  if (hasMultiplePolicies) {
    const items = filteredQuoteBindings?.flatMap((qb) => `#${qb.policyNumber}`);
    let policyNumbers: string;

    // build string, only bolding the policy numbers
    if (items.length > 2) {
      policyNumbers = `${items
        .slice(0, -2)
        .map((item) => `<b>${item}</b>`)
        .join(', ')}, <b>${items.slice(-2).join('</b> and <b>')}</b>`;
    } else if (items.length === 2) {
      policyNumbers = `<b>${items[0]}</b> and <b>${items[1]}</b>`;
    } else {
      policyNumbers = `<b>${items[0]}</b>`;
    }

    policyText = <span dangerouslySetInnerHTML={{ __html: `policies ${policyNumbers}` }} />;
  } else {
    policyText = (
      <span>
        policy{' '}
        <b>
          #
          {isEndorsement
            ? policy
              ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
              : '...'
            : quote?.policyNumber?.policyNumber}
        </b>{' '}
      </span>
    );
  }

  const messagePrefix = isEndorsement ? 'Final documents for' : 'Policy documents for';
  const content = [
    <span key='main-text'>
      {messagePrefix} <b>{quote?.customer.name}</b> {policyText} are generating and will be available for review within
      5-10 minutes.
    </span>,
  ];

  if (!isEndorsement) {
    content.push(
      <Box component='span' key='thanks-text' sx={{ fontWeight: isCustomerUser ? 'bold' : 'normal' }}>
        Thank you for choosing LIO!
      </Box>,
    );
  }

  return (
    <PortalMessage
      type={PortalMessageType.Bind}
      title={title}
      subtitle={content}
      secondaryBtnText={isCustomerUser ? undefined : 'Go to Policies'}
      secondaryBtnAction={isCustomerUser ? undefined : '/policies'}
      primaryBtnText={
        isCustomerUser
          ? undefined
          : quote.quoteType === QuoteType.Endorsement
            ? 'Return to Homepage'
            : 'Start Another Quote'
      }
      primaryBtnAction={isCustomerUser ? undefined : quote.quoteType === QuoteType.Endorsement ? '/' : '/new-quote'}
      quote={quote}
      multiPolicyQuoteBindings={hasMultiplePolicies ? filteredQuoteBindings : undefined}
    />
  );
};
